import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Description,
  Notifications,
  AssignmentReturn,
  DoneAll,
  Feedback,
  MonetizationOn,
  TaskAlt,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';

import ResignationStep from '../components/offboarding/ResignationStep';
import OffboardingRequestStep from '../components/offboarding/OffboardingRequestStep';
import NotificationStep from '../components/offboarding/NotificationStep';
import ExitInterviewStep from '../components/offboarding/ExitInterviewStep';
import CompletionStep from '../components/offboarding/CompletionStep';
import DeclarationStep from '../components/offboarding/DeclarationStep';

const stepDefs = [
  {label: 'Resignation Form', icon: <TaskAlt />},
  { label: 'Offboarding Request', icon: <Description /> },
  { label: 'Notification', icon: <Notifications /> },
  { label: 'Declaration', icon: <Description /> },
  { label: 'Exit Interview', icon: <Feedback /> },
  { label: 'Completion', icon: <TaskAlt /> },
];

const initialInterview = {
  employee_name: '',
  designation: '',
  doj: '',
  lwd: '',
  resignation_date: '',
  reporting_manager: '',
  department: '',
  enjoyed_most: '',
  enjoyed_least: '',
};

const Offboarding = () => {
  const { user, getAuthHeaders } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [resignationDate, setResignationDate] = useState('');
  const [discussionStatus, setDiscussionStatus] = useState('Not Discussed');
  const [discussionDate, setDiscussionDate] = useState('');
  const [notDiscussedComment, setNotDiscussedComment] = useState('');
  const [notified, setNotified] = useState(false);
  const [assets, setAssets] = useState([]);
  const [systemAccessRevoked, setSystemAccessRevoked] = useState(false);
  const [interview, setInterview] = useState(initialInterview);
  const [financialSettled, setFinancialSettled] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepValidation, setStepValidation] = useState({
    resignationForm: false,
    offboardingRequest: false,
    notification: false,
    declaration: false,
    exitInterview: false
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize offboarding progress and fetch current step
  useEffect(() => {
    const initializeProgress = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        // Initialize progress if not exists
        await axios.post(`${API_URL}/offboarding/progress/initialize`, {}, {
          headers: getAuthHeaders()
        });
        
        // Fetch current progress
        const response = await axios.get(`${API_URL}/offboarding/progress/current`, {
          headers: getAuthHeaders()
        });
        
        if (response.data.success) {
          setProgressData(response.data.data);
          setActiveStep(response.data.data.currentStep);
        }
      } catch (error) {
        console.error('Error initializing offboarding progress:', error);
        toast.error('Failed to load offboarding progress');
      } finally {
        setLoading(false);
      }
    };

    initializeProgress();
  }, [user, getAuthHeaders]);

  // Validate step completion by checking if user has completed required forms
  const validateStepCompletion = async () => {
    if (!user?.id) return;

    try {
      const validationResults = {
        resignationForm: false,
        offboardingRequest: false,
        notification: false,
        declaration: false,
        exitInterview: false
      };

      // Check Resignation Form
      try {
        const resignationResponse = await axios.get(`${API_URL}/offboarding/resignation/user/${user.id}`, {
          headers: getAuthHeaders()
        });
        validationResults.resignationForm = resignationResponse.status === 200;
      } catch (error) {
        validationResults.resignationForm = false;
      }

      // Check Offboarding Request
      try {
        const offboardingResponse = await axios.get(`${API_URL}/offboarding/offboarding-request/user/${user.id}`, {
          headers: getAuthHeaders()
        });
        validationResults.offboardingRequest = offboardingResponse.status === 200;
      } catch (error) {
        validationResults.offboardingRequest = false;
      }

      // Check Notification
      try {
        const notificationResponse = await axios.get(`${API_URL}/offboarding/notification/user/${user.id}`, {
          headers: getAuthHeaders()
        });
        validationResults.notification = notificationResponse.status === 200;
      } catch (error) {
        validationResults.notification = false;
      }

      // Check Declaration
      try {
        const declarationResponse = await axios.get(`${API_URL}/offboarding/declaration/user/${user.id}`, {
          headers: getAuthHeaders()
        });
        validationResults.declaration = declarationResponse.status === 200;
      } catch (error) {
        validationResults.declaration = false;
      }

      // Check Exit Interview Form (only if we're on or past the exit interview step)
      if (activeStep >= 4) {
        try {
          const exitInterviewResponse = await axios.get(`${API_URL}/offboarding/exit-form/user/${user.id}`, {
            headers: getAuthHeaders()
          });
          validationResults.exitInterview = exitInterviewResponse.status === 200;
        } catch (error) {
          // 404 is expected when no exit form exists yet, don't log as error
          if (error.response?.status !== 404) {
            console.error('Error checking exit interview form:', error);
          }
          validationResults.exitInterview = false;
        }
      }

      setStepValidation(validationResults);
    } catch (error) {
      console.error('Error validating step completion:', error);
    }
  };

  // Validate steps when user or activeStep changes
  useEffect(() => {
    validateStepCompletion();
  }, [user, activeStep, getAuthHeaders]);

  // Trigger validation when forms are submitted
  const triggerValidation = () => {
    validateStepCompletion();
  };

  const handleRequestChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'resignationDate') {
      setResignationDate(value);
    } else if (name === 'discussion.status') {
      setDiscussionStatus(value);
    } else if (name === 'discussion.discussionDate') {
      setDiscussionDate(value);
    } else if (name === 'discussion.notDiscussedComment') {
      setNotDiscussedComment(value);
    }
  };
  const handleNotified = (e) => setNotified(e.target.checked);
  const handleAssetChange = (e) => {
    const value = e.target.value;
    setAssets(typeof value === 'string' ? value.split(',') : value);
  };
  const handleSystemAccess = (e) => setSystemAccessRevoked(e.target.checked);
  const handleInterviewChange = (e) => {
    setInterview({ ...interview, [e.target.name]: e.target.value });
  };
  const handleFinancialSettled = (e) => setFinancialSettled(e.target.checked);
  const handleFinish = () => window.location.reload();

  // Save step completion to backend
  const saveStepCompletion = async (stepModule, completed, data = {}) => {
    try {
      await axios.post(`${API_URL}/offboarding/progress/step`, {
        userId: user.id,
        module: stepModule,
        completed,
        data
      }, {
        headers: getAuthHeaders()
      });
      
      // Refresh progress data
      const response = await axios.get(`${API_URL}/offboarding/progress/current`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        setProgressData(response.data.data);
      }
    } catch (error) {
      console.error('Error saving step completion:', error);
      toast.error('Failed to save step progress');
    }
  };

  // Handle step navigation with validation
  const handleNext = async () => {
    const currentStepModule = stepDefs[activeStep].label;
    
    // Save current step as completed
    await saveStepCompletion(currentStepModule, true);
    
    // Re-validate steps after completion
    await validateStepCompletion();
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const completedSteps = stepDefs.slice(0, 6).map((s) => s.label);

  const isStepCompleted = (idx) => {
    const label = stepDefs[idx].label;
    if (progressData?.steps && Array.isArray(progressData.steps)) {
      const match = progressData.steps.find((st) => st.module === label);
      if (match) return !!match.completed;
    }
    switch (idx) {
      case 0:
        return !!stepValidation.resignationForm;
      case 1:
        return !!stepValidation.offboardingRequest;
      case 2:
        return !!stepValidation.notification;
      case 3:
        return !!stepValidation.declaration;
      case 4:
        return !!stepValidation.exitInterview;
      case 5:
        return false;
      default:
        return false;
    }
  };

  const renderStepContent = (idx) => {
    switch (idx) {
      case 0:
        return <ResignationStep onFormSubmit={triggerValidation} />;
      case 1:
        return (
          <OffboardingRequestStep
            resignationDate={resignationDate}
            discussionStatus={discussionStatus}
            discussionDate={discussionDate}
            notDiscussedComment={notDiscussedComment}
            onChange={handleRequestChange}
            onFormSubmit={triggerValidation}
          />
        );
      case 2:
        return <NotificationStep onFormSubmit={triggerValidation} />;
      case 3:
        return <DeclarationStep onFormSubmit={triggerValidation} />;
      case 4:
        return <ExitInterviewStep interview={interview} onChange={handleInterviewChange} />;
      case 5:
        return <CompletionStep completedSteps={completedSteps} onFinish={handleFinish} />;
      default:
        return null;
    }
  };

  const isNextDisabled = () => {
    switch (activeStep) {
      case 0: // Resignation Form - Use API validation
        return !stepValidation.resignationForm;
      case 1: // Offboarding Request - Use API validation
        return !stepValidation.offboardingRequest;
      case 2: // Notification - Use API validation
        return !stepValidation.notification;
      case 3: // Declaration - Use API validation
        return !stepValidation.declaration;
      case 4: // Exit Interview - Use API validation
        return !stepValidation.exitInterview;
      case 5: // Completion - Local validation
        return !financialSettled;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
        <Paper sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Employee Offboarding
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
          Please complete the exit formalities, submit documents, and share your feedback to help us ensure a smooth and hassle free transition.
          </Typography>

          <Box sx={{ overflowX: isMobile ? 'auto' : 'visible' }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel={!isMobile}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              sx={{
                minWidth: isMobile ? '100%' : '600px',
                my: 3,
                '& .MuiStepLabel-label': {
                  fontSize: '1rem',
                  fontWeight: 500,
                },
                '& .MuiStepIcon-root': {
                  width: 36,
                  height: 36,
                },
                '& .MuiStepConnector-root': {
                  top: '18px',
                },
                '& .MuiStepConnector-line': {
                  borderTopWidth: 2,
                },
              }}
            >
              {stepDefs.map((step, idx) => {
                const completed = isStepCompleted(idx);
                return (
                  <Step key={step.label} completed={completed} disabled={!completed}>
                    <StepLabel
                      StepIconProps={{ sx: { fontSize: '1.5rem' } }}
                      icon={idx + 1}
                      onClick={completed ? () => setActiveStep(idx) : undefined}
                      sx={{
                        cursor: completed ? 'pointer' : 'not-allowed',
                        '&:hover': completed ? { opacity: 0.85 } : {}
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>

          <Box sx={{ mt: 4, width: '100%' }}>
            {loading && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Validating step completion...
                </Typography>
              </Box>
            )}
            {renderStepContent(activeStep)}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              fullWidth={isMobile}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled() || activeStep === stepDefs.length - 1}
              fullWidth={isMobile}
            >
              Next
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Offboarding;
